import axios from 'axios'

export interface User {
  id: string
  email: string
  name: string
  role: 'user' | 'creator' | 'admin' | 'god'
  permissions: string[]
  avatar?: string
  createdAt: string
  updatedAt: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface AuthResponse {
  user: User
  token: string
  refreshToken: string
}

class AuthService {
  private baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
  private token: string | null = null
  private user: User | null = null

  constructor() {
    this.initializeFromStorage()
  }

  private initializeFromStorage() {
    const token = localStorage.getItem('auth_token')
    const user = localStorage.getItem('auth_user')
    
    if (token && user) {
      this.token = token
      this.user = JSON.parse(user)
    }
  }

  // Login
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${this.baseURL}/auth/login`, credentials, {
        withCredentials: true,
      })
      const { user, tokens } = response.data
      
      this.token = tokens.accessToken
      this.user = user
      
      localStorage.setItem('auth_token', tokens.accessToken)
      localStorage.setItem('auth_user', JSON.stringify(user))
      
      // Disparar evento para notificar mudanças
      window.dispatchEvent(new Event('auth-change'))
      
      return response.data
    } catch (error) {
      throw new Error('Falha no login. Verifique suas credenciais.')
    }
  }

  // Logout
  async logout(): Promise<void> {
    this.clearAuth()
    try {
      await axios.post(`${this.baseURL}/auth/logout`, {}, { withCredentials: true })
    } catch (error) {
      console.error('Erro no logout:', error)
    }
  }

  // Refresh token
  async refreshToken(): Promise<string> {
    try {
      const response = await axios.post(`${this.baseURL}/auth/refresh`, {}, {
        withCredentials: true,
      })

      const newAccessToken = response.data.tokens?.accessToken || response.data.token
      this.token = newAccessToken
      localStorage.setItem('auth_token', newAccessToken)

      return newAccessToken
    } catch (error) {
      this.clearAuth()
      throw new Error('Sessão expirada. Faça login novamente.')
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.user
  }

  // Get current token (always reads from storage to stay fresh after refresh)
  getToken(): string | null {
    const stored = localStorage.getItem('auth_token')
    if (stored !== this.token) {
      this.token = stored
    }
    return this.token
  }

  // Update access token (called by apiClient after refresh)
  setToken(token: string): void {
    this.token = token
    localStorage.setItem('auth_token', token)
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token && !!this.user
  }

  // Check if user has specific permission
  hasPermission(permission: string): boolean {
    return this.user?.permissions.includes(permission) || false
  }

  // Check if user has any of the permissions
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission))
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    return this.user?.role === role
  }

  // Check if user is admin or higher
  isAdmin(): boolean {
    return this.user?.role === 'admin' || this.user?.role === 'god'
  }

  // Check if user is creator or higher
  isCreator(): boolean {
    return ['creator', 'admin', 'god'].includes(this.user?.role || '')
  }

  // Clear auth data
  private clearAuth() {
    this.token = null
    this.user = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    
    // Disparar evento para notificar mudanças
    window.dispatchEvent(new Event('auth-change'))
  }

  // Update user data
  updateUser(userData: Partial<User>) {
    if (this.user) {
      this.user = { ...this.user, ...userData }
      localStorage.setItem('auth_user', JSON.stringify(this.user))
    }
  }
}

export const authService = new AuthService()
export default authService
